import React, { useState } from "react";
import { apiRequest } from "../../helpers/api";
import LeadModal from "./LeadModal";
import { fromLocalInput, leadName, presetDate, taskPriorities, taskTypes, toLocalInput } from "./leadShared";

const presets = [["Tomorrow", 1], ["In 3 days", 3], ["Next week", 7], ["In 2 weeks", 14]];

// Create or edit a task on a lead. Saving returns the refreshed lead so callers can update in place.
const FollowUpModal = ({ lead, task, onClose, onSaved }) => {
  const editing = Boolean(task?.id);
  const [values, setValues] = useState(() => ({
    title: task?.title || `Follow up with ${leadName(lead)}`,
    type: task?.type || "FOLLOW_UP",
    priority: task?.priority || "MEDIUM",
    dueAt: toLocalInput(task?.dueAt || (editing ? null : presetDate(1))),
    description: task?.description || ""
  }));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setValues((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!values.title.trim()) { setError("Give the task a title."); return; }
    setBusy(true); setError("");
    try {
      const body = JSON.stringify({ ...values, title: values.title.trim(), dueAt: fromLocalInput(values.dueAt) });
      const result = await apiRequest(editing ? `/api/leads/${lead.id}/tasks/${task.id}` : `/api/leads/${lead.id}/tasks`, { method: editing ? "PATCH" : "POST", body });
      onSaved(result.lead, editing);
    } catch (requestError) {
      setError(requestError.message || "Unable to save this task.");
      setBusy(false);
    }
  };

  return <LeadModal as="form" onSubmit={submit} size="md" busy={busy} onClose={onClose}
    eyebrow={editing ? "Edit task" : "Schedule follow-up"} title={editing ? task.title : leadName(lead)}
    footer={<><button type="button" className="leads-secondary-button" onClick={onClose} disabled={busy}>Cancel</button><button type="submit" className="leads-primary-button" disabled={busy}>{busy ? <><i className="ti ti-loader-2 leads-spin" />Saving…</> : editing ? "Save task" : <><i className="ti ti-calendar-plus" />Schedule</>}</button></>}>
    {error && <div className="leads-inline-alert is-error" role="alert"><i className="ti ti-alert-circle" /><span>{error}</span></div>}
    <label className={`leads-field${error && !values.title.trim() ? " has-error" : ""}`}><span>Title</span><input value={values.title} maxLength={200} onChange={(event) => set("title", event.target.value)} /></label>
    <div className="leads-form-grid-2">
      <label className="leads-field"><span>Type</span><select value={values.type} onChange={(event) => set("type", event.target.value)}>{taskTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label className="leads-field"><span>Priority</span><select value={values.priority} onChange={(event) => set("priority", event.target.value)}>{taskPriorities.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
    </div>
    <div className="leads-field"><span>Due</span>
      <div className="leads-preset-row">{presets.map(([label, days]) => { const value = toLocalInput(presetDate(days)); return <button type="button" key={label} className={`leads-chip-button${values.dueAt === value ? " is-active" : ""}`} onClick={() => set("dueAt", value)}>{label}</button>; })}</div>
      <input type="datetime-local" aria-label="Due date and time" value={values.dueAt} onChange={(event) => set("dueAt", event.target.value)} />
    </div>
    <label className="leads-field"><span>Details <em>optional</em></span><textarea rows="3" maxLength={5000} value={values.description} onChange={(event) => set("description", event.target.value)} placeholder="What should happen on this follow-up?" /></label>
  </LeadModal>;
};

export default FollowUpModal;
